import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronLeft, LockKeyhole, LogIn, UserRound } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ConsoleFrame } from "@/components/layout/MainLayout";
import { AccountFrame } from "@/components/layout/ConsoleShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/api/client";
import {
  fetchInquiryPrivacyPolicy,
  submitInquiry,
  type InquiryCreateRequest,
  type InquiryPrivacyPolicy,
} from "@/api/inquiries";

const DRAFT_KEY = "kdrive.contact.logged-in-draft.v1";
const ATTEMPT_KEY = "kdrive.contact.submission-attempt.v1";
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EXECUTABLE_MARKUP = /<\s*script\b|javascript\s*:|\bon[a-z]+\s*=/iu;

type Fields = {
  familyName: string;
  givenName: string;
  email: string;
  inquiryType: "" | "product_service" | "plan" | "technical" | "partner" | "quote" | "other";
  content: string;
  accepted: boolean;
};

const emptyFields: Fields = {
  familyName: "",
  givenName: "",
  email: "",
  inquiryType: "",
  content: "",
  accepted: false,
};

export function ContactPage() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [guestSelected, setGuestSelected] = useState(false);
  const [loggedInIntent, setLoggedInIntent] = useState(false);
  const [fields, setFields] = useState<Fields>(emptyFields);
  const [loggedInDraftRestored, setLoggedInDraftRestored] = useState(false);
  const [policy, setPolicy] = useState<InquiryPrivacyPolicy | null>(null);
  const [policyError, setPolicyError] = useState("");
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ inquiryId: string; receivedAt: string } | null>(null);
  const [attempt, setAttempt] = useState<{ fingerprint: string; key: string } | null>(() => restoreAttempt());

  useEffect(() => {
    let cancelled = false;
    if (isAuthenticated) {
      setLoggedInIntent(true);
      if (!loggedInDraftRestored && user?.accountId) {
        void restoreLoggedInDraft(user.accountId).then((draft) => {
          if (!cancelled) {
            setFields(draft);
            setLoggedInDraftRestored(true);
          }
        });
      }
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, loggedInDraftRestored, user?.accountId]);

  useEffect(() => {
    let cancelled = false;
    fetchInquiryPrivacyPolicy().then((value) => {
      if (!cancelled) {
        setPolicy(value);
        setPolicyError("");
      }
    }).catch(() => {
      if (!cancelled) setPolicyError("個人情報の取り扱いを確認できません。時間をおいて再読み込みしてください。");
    });
    return () => { cancelled = true; };
  }, []);

  const sourceKind = isAuthenticated ? "logged_in"
    : loggedInIntent ? "login_required"
      : guestSelected ? "guest" : "choice";
  const canonicalPayload = useMemo(() => JSON.stringify(isAuthenticated ? {
    sourceKind: "logged_in",
    accountId: user?.accountId,
    inquiryType: fields.inquiryType,
    content: fields.content.replace(/\r\n?/g, "\n").trim(),
    policyVersion: policy?.version,
  } : {
    sourceKind: "guest",
    familyName: fields.familyName.trim(),
    givenName: fields.givenName.trim(),
    email: fields.email.trim().toLowerCase(),
    inquiryType: fields.inquiryType,
    content: fields.content.replace(/\r\n?/g, "\n").trim(),
    policyVersion: policy?.version,
  }), [fields, isAuthenticated, policy?.version, user?.accountId]);

  const goToLogin = () => {
    navigate("/login", { state: { from: { pathname: "/contact" } } });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    setSubmitError("");
    const nextErrors = validate(fields, sourceKind === "guest");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !policy) {
      const firstField = Object.keys(nextErrors)[0] as keyof Fields | undefined;
      const firstId = firstField === "email" ? "contactEmail"
        : firstField === "accepted" ? "privacyAccepted" : firstField;
      if (firstId) requestAnimationFrame(() => document.getElementById(firstId)?.focus());
      return;
    }
    if (!isAuthenticated && sourceKind !== "guest") return;
    const request = (isAuthenticated
      ? {
          sourceKind: "logged_in",
          inquiryType: fields.inquiryType,
          content: fields.content.trim(),
          privacyPolicyAccepted: true,
          privacyPolicyVersion: policy.version,
        }
      : {
          sourceKind: "guest",
          familyName: fields.familyName.trim(),
          givenName: fields.givenName.trim(),
          email: fields.email.trim().toLowerCase(),
          inquiryType: fields.inquiryType,
          content: fields.content.trim(),
          privacyPolicyAccepted: true,
          privacyPolicyVersion: policy.version,
        }) as InquiryCreateRequest;
    setIsSubmitting(true);
    try {
      const fingerprint = await sha256(canonicalPayload);
      const currentAttempt = attempt?.fingerprint === fingerprint
        ? attempt
        : { fingerprint, key: crypto.randomUUID() };
      setAttempt(currentAttempt);
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(currentAttempt));
      const response = await submitInquiry(request, currentAttempt.key);
      setAttempt(null);
      sessionStorage.removeItem(ATTEMPT_KEY);
      sessionStorage.removeItem(DRAFT_KEY);
      setResult(response);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && isAuthenticated) {
        await saveLoggedInDraft(fields, user?.accountId);
        setLoggedInIntent(true);
        setSubmitError("ログインの有効期限が切れました。再ログイン後、この問い合わせに戻って送信してください。");
      } else if (error instanceof ApiError && error.code === "inquiry_privacy_policy_changed") {
        setFields((current) => ({ ...current, accepted: false }));
        setPolicy(null);
        setSubmitError("個人情報の取り扱いが更新されました。ページを再読み込みして再度ご確認ください。");
      } else if (error instanceof ApiError && error.status === 429) {
        setSubmitError("送信回数が上限に達しました。15分後に同じ内容で再試行してください。");
      } else {
        const requestId = error instanceof ApiError && error.requestId
          ? `（受付確認ID: ${error.requestId}）` : "";
        setSubmitError(`受付結果を確認できませんでした。同じ内容のまま再試行してください。${requestId}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PublicLayout>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          ログイン状態を確認しています…
        </p>
      </PublicLayout>
    );
  }

  const content = result ? (
    <div className="w-full space-y-6">
      <PageHeader title="お問い合わせを受け付けました" description="確認のため、次の受付番号をお控えください。" />
      <div className="max-w-3xl">
      <div className="border-t-4 border-primary bg-background py-8 sm:px-8">
          <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden="true" />
          <div className="mt-8 border-y py-6">
            <p className="text-xs text-muted-foreground">問い合わせ受付番号</p>
            <p className="mt-2 break-all font-mono text-xl font-semibold">{Number.parseInt(result.inquiryId.slice(0, 4), 16)}</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">受付日時: {new Date(result.receivedAt).toLocaleString("ja-JP")}</p>
          <Button className="mt-8" onClick={() => navigate(isAuthenticated ? "/console/dashboard" : "/login")}>戻る</Button>
      </div>
      </div>
    </div>
  ) : (
      <div className="w-full space-y-6">
        <PageHeader title="お問い合わせ" description="EXTONE Driveの製品、導入、技術的なご相談を受け付けています。秘密情報は入力しないでください。" />
        <div className="max-w-3xl">
        <Link to={isAuthenticated ? "/console/dashboard" : "/login"} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" />戻る
        </Link>

        {sourceKind === "choice" ? (
          <section className="mt-12 border-t pt-8" aria-labelledby="source-heading">
            <h2 id="source-heading" className="text-xl font-semibold">問い合わせ元を選択</h2>
            <p className="mt-2 text-sm text-muted-foreground">アカウントをお持ちの場合は、ログインすると登録済みの連絡先を利用できます。</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <button type="button" onClick={() => { setLoggedInIntent(true); goToLogin(); }} className="group rounded-md border bg-background p-6 text-left transition-colors hover:border-primary/60 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <LogIn className="h-6 w-6 text-primary" />
                <span className="mt-5 block text-lg font-semibold">ログインして問い合わせる</span>
                <span className="mt-2 block text-sm leading-6 text-muted-foreground">登録済みの氏名とメールアドレスを問い合わせ元として使用します。</span>
              </button>
              <button type="button" onClick={() => setGuestSelected(true)} className="group rounded-md border bg-background p-6 text-left transition-colors hover:border-primary/60 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <UserRound className="h-6 w-6 text-primary" />
                <span className="mt-5 block text-lg font-semibold">ゲストとして問い合わせる</span>
                <span className="mt-2 block text-sm leading-6 text-muted-foreground">この問い合わせへの回答に必要な氏名とメールアドレスを入力します。</span>
              </button>
            </div>
          </section>
        ) : sourceKind === "login_required" ? (
          <section className="mt-12 border-l-4 border-amber-500 bg-amber-50 p-6" role="alert">
            <h2 className="font-semibold">再ログインが必要です</h2>
            <p className="mt-2 text-sm leading-6">入力内容をゲスト問い合わせには切り替えません。再ログインするとこの画面へ戻ります。</p>
            <Button className="mt-5" onClick={goToLogin}><LogIn className="mr-2 h-4 w-4" />再ログイン</Button>
          </section>
        ) : (
          <form onSubmit={handleSubmit} className="relative mt-12 space-y-10" noValidate>
            <section className="border-t pt-8" aria-labelledby="sender-heading">
              <div className="flex items-center justify-between gap-4">
                <h2 id="sender-heading" className="text-xl font-semibold">1. 問い合わせ元</h2>
                {sourceKind === "guest" && <button type="button" className="text-sm text-primary underline" onClick={() => setGuestSelected(false)}>選択し直す</button>}
              </div>
              {isAuthenticated ? (
                <div className="mt-5 grid gap-4 rounded-md border bg-muted/30 p-5 sm:grid-cols-2">
                  <div><p className="text-xs text-muted-foreground">氏名（変更不可）</p><p className="mt-1 font-medium">{user?.fullName}</p></div>
                  <div><p className="text-xs text-muted-foreground">メールアドレス（変更不可）</p><p className="mt-1 break-all font-medium">{user?.email}</p></div>
                  <p className="flex items-center gap-2 text-xs text-muted-foreground sm:col-span-2"><LockKeyhole className="h-3.5 w-3.5" />送信時にログイン中のアカウントから再確認します。</p>
                </div>
              ) : (
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field id="familyName" label="姓" value={fields.familyName} error={errors.familyName} onChange={(value) => update("familyName", value)} autoComplete="family-name" />
                  <Field id="givenName" label="名" value={fields.givenName} error={errors.givenName} onChange={(value) => update("givenName", value)} autoComplete="given-name" />
                  <div className="sm:col-span-2"><Field id="contactEmail" label="メールアドレス" type="email" value={fields.email} error={errors.email} onChange={(value) => update("email", value)} autoComplete="email" /></div>
                </div>
              )}
            </section>

            <section className="border-t pt-8" aria-labelledby="content-heading">
              <h2 id="content-heading" className="text-xl font-semibold">2. お問い合わせ内容</h2>
              <div className="mt-5 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="inquiryType">問い合わせ種別</Label>
                  <select id="inquiryType" required value={fields.inquiryType} onChange={(e) => update("inquiryType", e.target.value as Fields["inquiryType"])} aria-invalid={Boolean(errors.inquiryType)} aria-describedby={errors.inquiryType ? "inquiryType-error" : undefined} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <option value="">選択してください</option>
                    <option value="product_service">製品・サービス</option><option value="plan">プラン</option><option value="technical">技術</option><option value="partner">販売パートナー制度</option><option value="quote">見積依頼</option><option value="other">その他</option>
                  </select>
                  {errors.inquiryType && <p id="inquiryType-error" role="alert" className="text-sm text-destructive">{errors.inquiryType}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">お問い合わせ内容</Label>
                  <textarea id="content" required rows={9} value={fields.content} onChange={(e) => update("content", e.target.value)} aria-invalid={Boolean(errors.content)} aria-describedby={errors.content ? "content-error content-note" : "content-note"} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                  <div id="content-note" className="flex justify-between gap-4 text-xs text-muted-foreground"><span>パスワード、APIトークン、認証コード、秘密鍵は入力しないでください。</span><span>{Array.from(fields.content).length}/5000</span></div>
                  {errors.content && <p id="content-error" role="alert" className="text-sm text-destructive">{errors.content}</p>}
                </div>
              </div>
            </section>

            <section className="border-t pt-8" aria-labelledby="consent-heading">
              <h2 id="consent-heading" className="text-xl font-semibold">3. 個人情報の取り扱い</h2>
              {policyError && <p role="alert" className="mt-4 border-l-4 border-destructive bg-destructive/5 p-4 text-sm text-destructive">{policyError}</p>}
              <label className="mt-5 flex items-start gap-3 rounded-md border bg-muted/30 p-5 text-sm leading-6">
                <input id="privacyAccepted" type="checkbox" checked={fields.accepted} onChange={(e) => update("accepted", e.target.checked)} className="mt-1 h-4 w-4" aria-invalid={Boolean(errors.accepted)} aria-describedby={errors.accepted ? "accepted-error" : undefined} disabled={!policy} />
                <span>{policy ? <><a href={policy.url} target="_blank" rel="noreferrer" className="font-medium text-primary underline">個人情報の取り扱いについて</a>（版: {policy.version}）を確認し、問い合わせ対応とサービス品質改善のための取り扱いに同意します。</> : "個人情報の取り扱いを読み込んでいます…"}</span>
              </label>
              {errors.accepted && <p id="accepted-error" role="alert" className="mt-2 text-sm text-destructive">{errors.accepted}</p>}
            </section>

            {submitError && <div role="alert" className="border-l-4 border-destructive bg-destructive/5 p-4 text-sm text-destructive">{submitError}{submitError.includes("再ログイン") && <div><Button type="button" variant="outline" className="mt-3" onClick={goToLogin}>再ログイン</Button></div>}</div>}
            <div className="flex items-center justify-end border-t pt-6">
              <Button type="submit" disabled={isSubmitting || !policy} className="min-w-40">{isSubmitting ? "送信しています…" : "問い合わせを送信"}</Button>
            </div>
            <p className="sr-only" aria-live="polite">{isSubmitting ? "問い合わせを送信しています" : ""}</p>
          </form>
        )}
        </div>
      </div>
  );

  if (isAuthenticated && user?.currentMembership) {
    return (
      <ConsoleFrame activeCustomerId={user.currentMembership.customerId}>
        <div className="console-page">
          {content}
        </div>
      </ConsoleFrame>
    );
  }

  if (isAuthenticated) {
    return <AccountFrame><div className="console-page">{content}</div></AccountFrame>;
  }

  return (
    <PublicLayout contentClassName="max-w-none" align="start">
      {content}
    </PublicLayout>
  );

  function update<K extends keyof Fields>(key: K, value: Fields[K]) {
    setFields((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }
}

function Field(props: { id: string; label: string; value: string; error?: string; type?: string; autoComplete?: string; onChange: (value: string) => void }) {
  const errorId = `${props.id}-error`;
  return <div className="space-y-2"><Label htmlFor={props.id}>{props.label}</Label><Input id={props.id} type={props.type} required maxLength={props.id === "contactEmail" ? 254 : undefined} autoComplete={props.autoComplete} value={props.value} onChange={(e) => props.onChange(e.target.value)} aria-invalid={Boolean(props.error)} aria-describedby={props.error ? errorId : undefined} />{props.error && <p id={errorId} role="alert" className="text-sm text-destructive">{props.error}</p>}</div>;
}

function validate(fields: Fields, guest: boolean) {
  const errors: Partial<Record<keyof Fields, string>> = {};
  if (guest) {
    if (!fields.familyName.trim() || Array.from(fields.familyName.trim()).length > 100) errors.familyName = "姓を1〜100文字で入力してください。";
    if (!fields.givenName.trim() || Array.from(fields.givenName.trim()).length > 100) errors.givenName = "名を1〜100文字で入力してください。";
    const email = fields.email.trim();
    if (!EMAIL.test(email) || email.length > 254) errors.email = "連絡可能なメールアドレスを入力してください。";
  }
  if (!fields.inquiryType) errors.inquiryType = "問い合わせ種別を選択してください。";
  const content = fields.content.trim();
  if (!content || Array.from(content).length > 5000) errors.content = "お問い合わせ内容を1〜5000文字で入力してください。";
  else if (EXECUTABLE_MARKUP.test(content)) errors.content = "実行可能なマークアップは入力できません。";
  if (!fields.accepted) errors.accepted = "個人情報の取り扱いへの同意が必要です。";
  return errors;
}

async function saveLoggedInDraft(fields: Fields, accountId: string | undefined) {
  if (!accountId) return;
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
    accountDigest: await sha256(accountId),
    inquiryType: fields.inquiryType,
    content: fields.content,
  }));
}

async function restoreLoggedInDraft(accountId: string): Promise<Fields> {
  try {
    const value = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "null") as {
      accountDigest?: unknown;
      inquiryType?: Fields["inquiryType"];
      content?: unknown;
    } | null;
    if (!value || value.accountDigest !== await sha256(accountId)) return emptyFields;
    return {
      ...emptyFields,
      inquiryType: value.inquiryType ?? "",
      content: typeof value.content === "string" ? value.content : "",
    };
  } catch {
    return emptyFields;
  }
}

function restoreAttempt() {
  try {
    const value = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) ?? "null") as unknown;
    if (!value || typeof value !== "object") return null;
    const candidate = value as { fingerprint?: unknown; key?: unknown };
    return typeof candidate.fingerprint === "string" &&
      /^[0-9a-f]{64}$/.test(candidate.fingerprint) &&
      typeof candidate.key === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(candidate.key)
      ? { fingerprint: candidate.fingerprint, key: candidate.key }
      : null;
  } catch {
    return null;
  }
}

async function sha256(value: string) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
